import { create } from "zustand";
import { syncApi, type SyncTransport } from "../api.ts";
import { useStore } from "../store.ts";
import { deriveIdentity, decryptPayload, encryptPayload, type SyncIdentity } from "./crypto.ts";
import { isSyncPayload, mergeSync, toPayload, type SyncPayload, type SyncSlice } from "./merge.ts";

/**
 * Keeps this device and the shared copy in step.
 *   connect(code)  join (or start) a sync group: fetch, merge, write back
 *   pull()         on launch and when the app comes back to the foreground
 *   push()         a couple of seconds after anything synced changes
 * Every write is compare-and-set on a version; a 409 means another device wrote first, so we merge its copy and try again.
 * Nothing here ever blocks the game: failures are shown in the sync card and retried next time.
 */

export type SyncState = "off" | "idle" | "syncing" | "error";
interface Status {
  state: SyncState;
  error: string | null;
}
export const useSyncStatus = create<Status>()(() => ({ state: "off", error: null }));

export const sliceOf = (s: SyncSlice): SyncSlice => ({ stars: s.stars, playedDays: s.playedDays, streaks: s.streaks, received: s.received, sent: s.sent, senderName: s.senderName, translation: s.translation, changelogSeen: s.changelogSeen, seenIntro: s.seenIntro });

/** A stable string for "has anything that syncs changed?" (key order and array order normalised). */
export function fingerprint(s: SyncSlice): string {
  const sorted = (o: Record<string, number>) => Object.keys(o).sort().map((k) => `${k}:${o[k]}`).join(",");
  return [sorted(s.stars), [...s.playedDays].sort().join(","), s.streaks.longestStreak, sorted(s.received), [...s.sent].map((r) => r.code).sort().join(","), s.senderName, s.translation, s.changelogSeen ?? "", s.seenIntro ? 1 : 0].join("|");
}

const message = (e: unknown) => (e instanceof Error && e.message ? e.message : "Sync didn't go through. It will try again.");
const MAX_RETRIES = 3;

export class SyncEngine {
  private identity: (SyncIdentity & { code: string }) | null = null;
  private applying = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private inFlight: Promise<void> | null = null;
  private lastFingerprint = "";
  private stop: (() => void) | undefined;

  constructor(
    private readonly transport: SyncTransport = syncApi,
    private readonly now: () => number = () => Date.now(),
    private readonly debounceMs = 2500,
  ) {}

  private get store() {
    return useStore.getState();
  }

  private async ident(): Promise<SyncIdentity> {
    const code = this.store.sync.code;
    if (!code) throw new Error("This device isn't set up to sync.");
    if (!this.identity || this.identity.code !== code) this.identity = { ...(await deriveIdentity(code)), code };
    return this.identity;
  }

  private set(status: Partial<Status>) {
    useSyncStatus.setState(status);
  }

  /** Write a merged slice into the store without the change-listener treating it as a local edit. */
  private apply(slice: SyncSlice): void {
    if (fingerprint(slice) === fingerprint(sliceOf(this.store))) return;
    this.applying = true;
    try {
      useStore.setState(slice);
      this.lastFingerprint = fingerprint(slice);
    } finally {
      this.applying = false;
    }
  }

  private async decrypt(id: SyncIdentity, body: string): Promise<SyncPayload> {
    let payload: unknown;
    try {
      payload = await decryptPayload(id.key, body);
    } catch {
      throw new Error("The synced copy couldn't be read with this code.");
    }
    if (!isSyncPayload(payload)) throw new Error("The synced copy isn't in a shape this version understands.");
    return payload;
  }

  /** Merge a remote copy in, write the result back if we added anything, and remember the version we're at. */
  private async reconcile(id: SyncIdentity, remote: { body: string; version: number } | null, pushAnyway: boolean): Promise<void> {
    let version = remote ? remote.version : this.store.sync.version;
    let merged: SyncSlice;
    if (remote) {
      const payload = await this.decrypt(id, remote.body);
      merged = mergeSync(sliceOf(this.store), this.store.sync.localChangedAt, payload, new Date(this.now()));
      this.apply(merged);
      // Only write back if the merge produced something the shared copy doesn't have yet.
      if (!pushAnyway && fingerprint(merged) === fingerprint(mergeSync(sliceOf({ ...merged, playedDays: [], stars: {}, received: {}, sent: [], streaks: { currentStreak: 0, longestStreak: 0, currentStreakStart: 0 }, seenIntro: false, changelogSeen: null, senderName: "", translation: "WEB" }), 0, payload, new Date(this.now())))) {
        this.store.setSync({ version, lastSyncedAt: this.now() });
        return;
      }
    } else {
      merged = sliceOf(this.store);
    }
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      const body = await encryptPayload(id.key, toPayload(merged, this.store.sync.localChangedAt || this.now()));
      const r = await this.transport.put(id.id, id.auth, body, version);
      if (r.ok) {
        this.store.setSync({ version: r.version, lastSyncedAt: this.now() });
        return;
      }
      if (!r.current) {
        version = 0; // the copy was deleted under us: start again from scratch
        continue;
      }
      const theirs = await this.decrypt(id, r.current.body);
      merged = mergeSync(sliceOf(this.store), this.store.sync.localChangedAt, theirs, new Date(this.now()));
      this.apply(merged);
      version = r.current.version;
    }
    throw new Error("Other devices kept writing at the same time. It will try again shortly.");
  }

  private run(work: (id: SyncIdentity) => Promise<void>): Promise<void> {
    // One operation at a time; a second request while one is running waits for it, then runs.
    const next = (this.inFlight ?? Promise.resolve()).then(async () => {
      this.set({ state: "syncing", error: null });
      try {
        await work(await this.ident());
        this.set({ state: this.store.sync.code ? "idle" : "off", error: null });
      } catch (e) {
        this.set({ state: this.store.sync.code ? "error" : "off", error: message(e) });
        throw e;
      }
    });
    this.inFlight = next.catch(() => undefined).finally(() => {
      if (this.inFlight === done) this.inFlight = null;
    });
    const done = this.inFlight;
    return next;
  }

  /** Join (or start) the group for `code`. Resolves once this device holds the merged result and the server does too. */
  connect(code: string): Promise<void> {
    this.store.setSync({ code, version: 0 });
    this.identity = null;
    return this.run(async (id) => this.reconcile(id, await this.transport.get(id.id, id.auth), true));
  }

  pull(): Promise<void> {
    return this.run(async (id) => {
      const remote = await this.transport.get(id.id, id.auth);
      if (remote && remote.version === this.store.sync.version && fingerprint(sliceOf(this.store)) === this.lastFingerprint) {
        this.store.setSync({ lastSyncedAt: this.now() });
        return;
      }
      await this.reconcile(id, remote, false);
      this.lastFingerprint = fingerprint(sliceOf(this.store));
    });
  }

  push(): Promise<void> {
    return this.run(async (id) => {
      await this.reconcile(id, null, true);
      this.lastFingerprint = fingerprint(sliceOf(this.store));
    });
  }

  /** Forget the code on this device. Progress stays; the shared copy stays for the other devices. */
  disconnect(): void {
    clearTimeout(this.timer);
    this.store.setSync({ code: null, version: 0, lastSyncedAt: null });
    this.identity = null;
    this.set({ state: "off", error: null });
  }

  /** Remove the shared copy from the server, then forget the code here. Other devices keep their local progress. */
  async deleteRemote(): Promise<void> {
    await this.run(async (id) => this.transport.del(id.id, id.auth));
    this.disconnect();
  }

  /** Watch the store: anything synced that changes schedules a push. Returns a stop function. */
  start(): () => void {
    this.stop?.();
    this.lastFingerprint = fingerprint(sliceOf(this.store));
    const unsubscribe = useStore.subscribe((s) => {
      if (this.applying || !s.sync.code) return;
      const fp = fingerprint(sliceOf(s));
      if (fp === this.lastFingerprint) return;
      this.lastFingerprint = fp;
      s.setSync({ localChangedAt: this.now() });
      clearTimeout(this.timer);
      this.timer = setTimeout(() => void this.push().catch(() => undefined), this.debounceMs);
    });
    this.stop = () => {
      unsubscribe();
      clearTimeout(this.timer);
    };
    if (this.store.sync.code) this.set({ state: "idle", error: null });
    return this.stop;
  }
}

export const syncEngine = new SyncEngine();
